import React, { useEffect, useState } from "react";
import { Ticket, Loader2 } from "lucide-react";
import { fetchAdminCoupons } from "@/lib/api";

/**
 * Compact live view of Umbra-managed Stripe coupon batches. Shown at the
 * top of /admin so redemption momentum is visible at a glance.
 */
export default function CouponDashboardCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setData(await fetchAdminCoupons());
      } catch (e) {
        setErr("Could not load coupon inventory");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const batches = data?.batches || [];
  const totals = batches.reduce(
    (acc, b) => {
      const codes = b.codes || [];
      const redeemed = codes.filter((c) => (c.times_redeemed || 0) > 0).length;
      acc.remaining += b.codes_remaining || codes.length - redeemed;
      acc.redeemed += redeemed;
      acc.total += codes.length;
      return acc;
    },
    { remaining: 0, redeemed: 0, total: 0 }
  );

  return (
    <div
      className="border border-obsidian-600 p-6 mb-10 bg-obsidian-800/30"
      data-testid="admin-coupon-card"
    >
      <div className="flex items-center gap-3 mb-4">
        <Ticket className="h-4 w-4 text-terracotta" />
        <p className="font-sans uppercase tracking-[0.28em] text-[0.65rem] text-terracotta">
          Coupon inventory · live from Stripe
        </p>
      </div>

      {loading && (
        <div className="flex items-center gap-3 text-bone-400 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> loading
        </div>
      )}
      {err && <p className="font-sans text-sm text-red-400">{err}</p>}

      {!loading && !err && batches.length === 0 && (
        <p className="font-sans text-sm text-bone-500">
          No Umbra-managed coupon batches yet. Run{" "}
          <code className="text-bone-300">setup_stripe.py</code> to seed comp
          batches.
        </p>
      )}

      {!loading && !err && batches.length > 0 && (
        <>
          <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-5" data-testid="admin-coupon-totals">
            <div>
              <p className="font-sans text-[0.55rem] sm:text-[0.6rem] uppercase tracking-[0.22em] text-bone-500 mb-1">
                Live codes
              </p>
              <p className="font-serif text-2xl sm:text-3xl text-bone-100">
                {totals.remaining}
              </p>
            </div>
            <div>
              <p className="font-sans text-[0.55rem] sm:text-[0.6rem] uppercase tracking-[0.22em] text-bone-500 mb-1">
                Redeemed
              </p>
              <p className="font-serif text-2xl sm:text-3xl text-terracotta">
                {totals.redeemed}
              </p>
            </div>
            <div>
              <p className="font-sans text-[0.55rem] sm:text-[0.6rem] uppercase tracking-[0.22em] text-bone-500 mb-1">
                Batches
              </p>
              <p className="font-serif text-2xl sm:text-3xl text-bone-100">
                {batches.length}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            {batches.map((b) => {
              const codes = b.codes || [];
              const redeemed = codes.filter((c) => (c.times_redeemed || 0) > 0).length;
              const remaining = b.codes_remaining ?? codes.length - redeemed;
              const total = codes.length;
              const pct = total > 0 ? Math.round((redeemed / total) * 100) : 0;
              return (
                <div
                  key={b.coupon_id}
                  data-testid={`admin-coupon-batch-${b.coupon_id}`}
                  className="grid grid-cols-1 sm:grid-cols-4 gap-3 py-2 border-t border-obsidian-600 items-center"
                >
                  <div className="sm:col-span-2">
                    <p className="font-serif text-sm text-bone-100 truncate">{b.name}</p>
                    <p className="font-mono text-[0.65rem] text-bone-500 truncate">
                      {b.coupon_id} ·{" "}
                      {b.percent_off ? `${b.percent_off}% off` : `$${(b.amount_off || 0) / 100} off`}{" "}
                      · {b.duration}
                    </p>
                  </div>
                  <div className="font-sans text-xs text-bone-300 text-right">
                    <span className="text-terracotta">{redeemed}</span> /{" "}
                    {total} redeemed
                  </div>
                  <div>
                    <div className="h-1 bg-obsidian-600 relative">
                      <div
                        className="absolute inset-y-0 left-0 bg-terracotta"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <p className="mt-1 font-sans text-[0.6rem] uppercase tracking-[0.22em] text-bone-500">
                      {remaining} live
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
